import { useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, FileSpreadsheet, Loader2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

/** Celda editable de mínimo/máximo por sucursal (Enter o salir del campo guarda). */
export function MinMaxCell({ fila, sucursalId, campo, onSaved }) {
  const key = campo === "minimo" ? "stock_minimo" : "stock_maximo";
  const [v, setV] = useState(fila[key] ?? "");
  const [busy, setBusy] = useState(false);
  async function guardar() {
    if (String(v) === String(fila[key] ?? "")) return;
    setBusy(true);
    try {
      const { data } = await api.put("/stock/minmax", { sucursal_id: sucursalId, componente_id: fila.componente_id, minimo: campo === "minimo" ? v : fila.stock_minimo, maximo: campo === "maximo" ? v : fila.stock_maximo });
      onSaved?.(data); toast.success(`${fila.sku}: mín ${data.stock_minimo ?? "—"} / máx ${data.stock_maximo ?? "—"}`);
    } catch (e) { toast.error(formatApiError(e)); setV(fila[key] ?? ""); } finally { setBusy(false); }
  }
  return (
    <input data-testid={`stock-${campo}-${fila.sku}`} type="number" min="0" step="any" value={v} disabled={busy} placeholder="—" title={fila.minmax_sucursal ? "Personalizado para esta sucursal" : "General del catálogo"}
      onClick={(e) => e.stopPropagation()} onChange={(e) => setV(e.target.value)} onBlur={guardar} onKeyDown={(e) => e.key === "Enter" && e.target.blur()}
      className={`field h-8 w-20 font-mono text-right px-2 text-xs ${fila.minmax_sucursal ? "border-brand-amber/70 bg-brand-amber-soft/40" : ""}`} />
  );
}

/** Botones: plantilla xlsx con los mín/máx actuales + importar archivo con SKU, Mínimo, Máximo. */
export function ImportarMinMax({ sucursalId, onDone }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  async function plantilla() {
    try { const r = await api.get("/stock/minmax/plantilla", { params: { sucursal_id: sucursalId }, responseType: "blob" }); const a = document.createElement("a"); a.href = URL.createObjectURL(r.data); a.download = "minimos_maximos.xlsx"; a.click(); } catch (e) { toast.error(formatApiError(e)); }
  }
  async function subir(e) {
    const file = e.target.files?.[0]; e.target.value = "";
    if (!file) return;
    setBusy(true);
    const fd = new FormData(); fd.append("file", file); if (sucursalId) fd.append("sucursal_id", sucursalId);
    try {
      const { data } = await api.post("/stock/minmax/importar", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Mín/Máx importados: ${data.actualizados} insumos${data.total_errores ? ` · ${data.total_errores} con error` : ""}`);
      if (data.errores?.length) toast.warning(data.errores.slice(0, 3).join(" · "), { duration: 9000 });
      onDone?.();
    } catch (er) { toast.error(formatApiError(er)); } finally { setBusy(false); }
  }
  return (
    <div className="flex items-center gap-2" data-testid="minmax-importar">
      <button data-testid="minmax-plantilla-btn" onClick={plantilla} className="btn-soft h-11 px-3 text-xs" title="Excel con los mínimos y máximos actuales de esta sucursal"><FileSpreadsheet className="w-4 h-4" /> Plantilla mín/máx</button>
      <button data-testid="minmax-importar-btn" onClick={() => ref.current?.click()} disabled={busy} className="btn-ink h-11 px-4 text-sm">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4 text-brand-amber" />} Importar mín/máx (xlsx)</button>
      <input ref={ref} data-testid="minmax-file-input" type="file" accept=".xlsx,.xlsm,.csv" className="hidden" onChange={subir} />
    </div>
  );
}
